import { useState } from "react"
import type { ChangeEvent, FormEvent } from "react"
import { Link } from "react-router-dom"
import DataTable from "@/components/common/DataTable"
import Loading from "@/components/common/Loading"
import Pagination from "@/components/common/Pagination"
import StatusBadge from "@/components/common/StatusBadge"
import { PAGE_SIZE } from "@/config/AppConfig"
import { useAdminTournamentList } from "@/domain/admin/hooks/AdminTournamentListHook"
import { TOURNAMENT_STATUS_OPTIONS } from "@/domain/tournament/model/Tournament"
import { sportLabel } from "@/domain/fight/model/Fight"
import type { TableColumn } from "@/components/common/DataTable"
import type { TournamentListItem } from "@/domain/tournament/model/Tournament"

export default function AdminTournamentListPage() {
  const {
    useAdminTournamentListState,
    setKeyword,
    setStatus,
    search,
    changePage,
    remove,
  } = useAdminTournamentList()
  const {
    tournaments,
    totalCount,
    currentPage,
    keyword,
    status,
    isLoaded,
    errorMessage,
  } = useAdminTournamentListState()

  const [removeTarget, setRemoveTarget] = useState<TournamentListItem | null>(
    null,
  )

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  function onChangeKeyword(event: ChangeEvent<HTMLInputElement>) {
    setKeyword(event.target.value)
  }

  function onChangeStatus(event: ChangeEvent<HTMLSelectElement>) {
    setStatus(event.target.value)
  }

  function onSubmitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void search()
  }

  function onClickCloseRemove() {
    setRemoveTarget(null)
  }

  function onClickConfirmRemove() {
    if (!removeTarget) return

    void remove(removeTarget.id).then(() => setRemoveTarget(null))
  }

  const columns: TableColumn<TournamentListItem>[] = [
    {
      dataField: "name",
      caption: "대회명",
      isTitle: true,
      cellTemplate: (row) => (
        <Link
          to={`/admin/tournaments/${row.id}`}
          className="focus-ring font-bold hover:text-app-primary-600"
        >
          {row.name}
        </Link>
      ),
    },
    { dataField: "periodText", caption: "대회일", width: 100 },
    { dataField: "venue", caption: "장소", width: 180 },
    {
      dataField: "sports",
      caption: "개최 종목",
      width: 340,
      cellTemplate: (row) => row.sports.map(sportLabel).join(", "),
    },
    {
      dataField: "entryCount",
      caption: "신청",
      alignment: "right",
      width: 90,
      cellTemplate: (row) => `${row.entryCount}건`,
    },
    {
      dataField: "statusLabel",
      caption: "상태",
      width: 110,
      cellTemplate: (row) => <StatusBadge label={row.statusLabel} />,
    },
    {
      dataField: "id",
      caption: "관리",
      alignment: "right",
      width: 140,
      cellTemplate: (row) => (
        <span className="flex justify-end gap-2">
          <Link
            to={`/admin/tournaments/${row.id}`}
            className="focus-ring app-btn app-btn-sm app-btn-outline"
          >
            수정
          </Link>
          <button
            type="button"
            onClick={() => setRemoveTarget(row)}
            className="focus-ring app-btn app-btn-sm app-btn-danger-outline"
          >
            삭제
          </button>
        </span>
      ),
    },
  ]

  return (
    <div className="app-page-admin">
      <section className="app-page-head flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="app-txt-eyebrow text-app-primary-600">TOURNAMENTS</p>
          <h2 className="app-txt-h1 mt-2">대회 관리</h2>
          <p className="app-txt-body2 mt-2 text-app-text-200">
            대회를 등록 · 수정하고 접수 상태를 관리합니다. 삭제하면 대회와 함께
            신청 · 대진 내역까지 사라지며 되돌릴 수 없습니다.
          </p>
        </div>
        <Link
          to="/admin/tournaments/new"
          className="focus-ring app-btn app-btn-primary shrink-0"
        >
          새 대회 등록
        </Link>
      </section>

      <form
        onSubmit={onSubmitSearch}
        className="app-card mb-5 flex flex-wrap items-end gap-3"
      >
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="admin-tournament-keyword"
            className="app-label-sub"
          >
            대회명
          </label>
          <input
            id="admin-tournament-keyword"
            type="text"
            value={keyword}
            onChange={onChangeKeyword}
            placeholder="대회명 검색"
            className="focus-ring app-field w-56"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="admin-tournament-status"
            className="app-label-sub"
          >
            상태
          </label>
          <select
            id="admin-tournament-status"
            value={status}
            onChange={onChangeStatus}
            className="focus-ring app-field w-auto"
          >
            <option value="">전체</option>
            {TOURNAMENT_STATUS_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
            {/* 삭제가 실제 삭제로 바뀌기 전에 취소해 둔 대회를 찾는 용도로 남긴다 */}
            <option value="TOURNAMENT_CANCELED">취소</option>
          </select>
        </div>

        <button
          type="submit"
          className="focus-ring app-btn app-btn-primary"
        >
          검색
        </button>
      </form>

      {errorMessage && (
        <p
          role="alert"
          className="app-alert app-alert-error mb-5"
        >
          {errorMessage}
        </p>
      )}

      {!isLoaded && <Loading label="대회 목록을 불러오는 중" />}

      {isLoaded && (
        <>
          <div className="app-surface overflow-x-auto">
            <DataTable
              rowIdField="id"
              dataSource={tournaments}
              columns={columns}
              caption="대회 목록"
              emptyMessage="등록된 대회가 없습니다."
            />
          </div>

          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={changePage}
            />
          </div>
        </>
      )}

      {removeTarget && (
        <div className="app-dialog-backdrop">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-tournament-title"
            className="app-dialog"
          >
            <h3
              id="remove-tournament-title"
              className="app-txt-h3"
            >
              대회를 삭제할까요?
            </h3>
            <p className="app-txt-body2 mt-3 leading-6 text-app-text-300">
              「{removeTarget.name}」와 여기에 접수된{" "}
              <strong>신청 {removeTarget.entryCount}건 · 편성한 대진</strong>이
              모두 지워집니다. <strong>되돌릴 수 없습니다.</strong>
            </p>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClickCloseRemove}
                className="focus-ring app-btn app-btn-outline"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={onClickConfirmRemove}
                className="focus-ring app-btn app-btn-danger"
              >
                삭제 확정
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
